// Hardcoded video examples with full scenes_data for VEO3 prompts
// This data is static and serves as fallback when database is unavailable
// Last synced with Admin data: 2026-01-29

export interface SceneData {
  sceneNumber: number;
  title: string;
  content: string;
}

export interface ScenesData {
  totalScenes: number;
  scenes: SceneData[];
}

export interface VideoExample {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  prompt_text: string | null;
  duration: string | null;
  display_order: number;
  scenes_data: ScenesData | null;
}

export const VIDEO_EXAMPLES: VideoExample[] = [
  {
    id: '66cb9ecd-d0e9-48b4-b377-be23b95ee431',
    title: 'Suits Ad',
    description: 'Showcase your new product with cinematic flair',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/1765700027456-004bf41da_17543060011.mp4',
    thumbnail_url: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=400&h=300&fit=crop',
    prompt_text: null,
    duration: '60s',
    display_order: 1,
    scenes_data: {
      totalScenes: 5,
      scenes: [
        {
          sceneNumber: 1,
          title: 'The First Impression',
          content: `Subject:
Ethan Blake: 38-year-old male, Caucasian, Ethan stands at an even six feet, his build athletic and well-maintained, suggesting a dedication to fitness without ostentation. His hair is dark brown, neatly styled with a subtle fade at the sides, hinting at both professionalism and a modern sensibility. Eyes of deep hazel hold a calm, assessing gaze, framed by slightly furrowed brows that speak to a thoughtful nature. His skin is clear and lightly tanned, indicative of an active outdoor life balanced with indoor responsibilities. A faint scar graces his left cheekbone, a subtle mark that adds character to his otherwise symmetrical features. He typically dresses in well-fitting, contemporary business attire – think tailored shirts, slim-cut trousers, and understated leather shoes. His posture is upright and confident, yet relaxed, conveying an air of approachable authority. He wears a simple, elegant wristwatch, a sign of his appreciation for quality and timeless design. His hands are well-kept, with short, clean nails, suggesting attention to detail without being overly meticulous. Overall, Ethan projects an image of success, sophistication, and quiet confidence.

Action: Ethan enters the NOR showroom, looking around at the minimalist design.

Dialogue:


Context: Ethan enters the showroom and takes in the atmosphere.

Location: The entrance is a study in minimalist design. A single, oversized glass door allows natural light to flood the interior. The facade is clad in smooth, grey stone, punctuated by a discreet NOR logo in brushed steel. Potted olive trees flank the entrance, adding a touch of organic warmth. The surrounding street is clean and upscale, hinting at the exclusivity of the location. A valet stand is subtly positioned to the side, further emphasizing the premium experience. - Late Afternoon, Autumn

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Wide shot establishing the showroom's elegance. Focus on natural light and clean lines.
Camera Movement: Smooth, cinematic movement supporting the Curious mood
Lighting: Professional lighting matching Stylized Corporate Elegance and Late Afternoon
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Curious emotion
SFX: Soft, ambient music begins. Subtle sounds of a high-end retail environment.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Curious

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 2,
          title: 'The Perfect Navy Fit',
          content: `Subject:
Ethan Blake: 38-year-old male, Caucasian, Ethan stands at an even six feet, his build athletic and well-maintained, suggesting a dedication to fitness without ostentation. His hair is dark brown, neatly styled with a subtle fade at the sides, hinting at both professionalism and a modern sensibility. Eyes of deep hazel hold a calm, assessing gaze, framed by slightly furrowed brows that speak to a thoughtful nature. His skin is clear and lightly tanned, indicative of an active outdoor life balanced with indoor responsibilities. A faint scar graces his left cheekbone, a subtle mark that adds character to his otherwise symmetrical features. He typically dresses in well-fitting, contemporary business attire – think tailored shirts, slim-cut trousers, and understated leather shoes. His posture is upright and confident, yet relaxed, conveying an air of approachable authority. He wears a simple, elegant wristwatch, a sign of his appreciation for quality and timeless design. His hands are well-kept, with short, clean nails, suggesting attention to detail without being overly meticulous. Overall, Ethan projects an image of success, sophistication, and quiet confidence.

Action: Ethan tries on the navy suit. He checks his reflection in the mirror, adjusting the jacket.

Dialogue:
[Ethan Blake]: "This feels really good."

Context: Ethan tries on the navy suit and admires the fit.

Location: The fitting room is spacious and well-lit, designed for comfort and privacy. Walls are clad in soft, neutral-toned fabric, creating a hushed atmosphere. A full-length mirror dominates one wall, reflecting the customer from every angle. Soft, directional lighting highlights the details of the clothing without being harsh. A comfortable leather armchair sits in one corner, providing a place for relaxation. Subtle, recessed lighting adds to the sense of understated luxury. The floor is covered in a plush, dark grey carpet, muffling sound and adding to the sense of intimacy. - N/A, N/A

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Medium shot focusing on the suit's tailoring and how it fits Ethan. Soft, directional lighting.
Camera Movement: Smooth, cinematic movement supporting the Appreciative mood
Lighting: Professional lighting matching Stylized Corporate Elegance and N/A
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
[Ethan Blake]: "This feels really good."
Music: Subtle background music enhancing Appreciative emotion
SFX: Subtle, upbeat music. Sound of fabric rustling as he adjusts the suit.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Appreciative

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 3,
          title: 'Details of Distinction',
          content: `Subject:
NOR Navy Wool Suit: The navy suit is crafted from fine Italian wool, with a subtle texture that catches the light. The color is a deep, classic navy, conveying sophistication and understated style. The jacket features a two-button closure, notch lapels, and a tailored fit that accentuates the wearer's physique. The trousers are slim-cut, with a clean break at the ankle. The stitching is impeccable, with attention paid to every detail. The buttons are made of genuine horn, adding a touch of luxury. The lining is a subtle, complementary color, enhancing the overall aesthetic.

Action: Close-up shots of Ethan adjusting his watch and tie, highlighting the suit's details.

Dialogue:


Context: Focus on styling details and Ethan's sense of control.

Location: The fitting room is spacious and well-lit, designed for comfort and privacy. Walls are clad in soft, neutral-toned fabric, creating a hushed atmosphere. A full-length mirror dominates one wall, reflecting the customer from every angle. Soft, directional lighting highlights the details of the clothing without being harsh. A comfortable leather armchair sits in one corner, providing a place for relaxation. Subtle, recessed lighting adds to the sense of understated luxury. The floor is covered in a plush, dark grey carpet, muffling sound and adding to the sense of intimacy. - N/A, N/A

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Extreme close-ups of stitching, buttons, and the watch/tie combination. Shallow depth of field.
Camera Movement: Smooth, cinematic movement supporting the Confident mood
Lighting: Professional lighting matching Stylized Corporate Elegance and N/A
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Confident emotion
SFX: Ticking of a watch. Subtle sound of fabric as he tightens his tie.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Confident

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 4,
          title: 'The Power Statement',
          content: `Subject:
Ethan Blake: 38-year-old male, Caucasian, Ethan stands at an even six feet, his build athletic and well-maintained, suggesting a dedication to fitness without ostentation. His hair is dark brown, neatly styled with a subtle fade at the sides, hinting at both professionalism and a modern sensibility. Eyes of deep hazel hold a calm, assessing gaze, framed by slightly furrowed brows that speak to a thoughtful nature. His skin is clear and lightly tanned, indicative of an active outdoor life balanced with indoor responsibilities. A faint scar graces his left cheekbone, a subtle mark that adds character to his otherwise symmetrical features. He typically dresses in well-fitting, contemporary business attire – think tailored shirts, slim-cut trousers, and understated leather shoes. His posture is upright and confident, yet relaxed, conveying an air of approachable authority. He wears a simple, elegant wristwatch, a sign of his appreciation for quality and timeless design. His hands are well-kept, with short, clean nails, suggesting attention to detail without being overly meticulous. Overall, Ethan projects an image of success, sophistication, and quiet confidence.

Action: Ethan tries on the black suit. He straightens his posture and looks intensely at his reflection.

Dialogue:
[Ethan Blake]: "Now this is a statement."

Context: Ethan transforms in the black suit, embodying formal elegance.

Location: The fitting room is spacious and well-lit, designed for comfort and privacy. Walls are clad in soft, neutral-toned fabric, creating a hushed atmosphere. A full-length mirror dominates one wall, reflecting the customer from every angle. Soft, directional lighting highlights the details of the clothing without being harsh. A comfortable leather armchair sits in one corner, providing a place for relaxation. Subtle, recessed lighting adds to the sense of understated luxury. The floor is covered in a plush, dark grey carpet, muffling sound and adding to the sense of intimacy. - N/A, N/A

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Full-length mirror shot showing the suit's silhouette. Lighting shifts to emphasize texture and form.
Camera Movement: Smooth, cinematic movement supporting the Powerful mood
Lighting: Professional lighting matching Stylized Corporate Elegance and N/A
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
[Ethan Blake]: "Now this is a statement."
Music: Subtle background music enhancing Powerful emotion
SFX: Music becomes slightly more intense and dramatic. Sound of fabric.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Powerful

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 5,
          title: 'Ready to Conquer',
          content: `Subject:
Ethan Blake: 38-year-old male, Caucasian, Ethan stands at an even six feet, his build athletic and well-maintained, suggesting a dedication to fitness without ostentation. His hair is dark brown, neatly styled with a subtle fade at the sides, hinting at both professionalism and a modern sensibility. Eyes of deep hazel hold a calm, assessing gaze, framed by slightly furrowed brows that speak to a thoughtful nature. His skin is clear and lightly tanned, indicative of an active outdoor life balanced with indoor responsibilities. A faint scar graces his left cheekbone, a subtle mark that adds character to his otherwise symmetrical features. He typically dresses in well-fitting, contemporary business attire – think tailored shirts, slim-cut trousers, and understated leather shoes. His posture is upright and confident, yet relaxed, conveying an air of approachable authority. He wears a simple, elegant wristwatch, a sign of his appreciation for quality and timeless design. His hands are well-kept, with short, clean nails, suggesting attention to detail without being overly meticulous. Overall, Ethan projects an image of success, sophistication, and quiet confidence.

Action: Ethan stands confidently at the showroom entrance, ready to step out. Final shot lingers on the impeccable fit.

Dialogue:


Context: Ethan, transformed, prepares to face the world with confidence.

Location: The entrance is a study in minimalist design. A single, oversized glass door allows natural light to flood the interior. The facade is clad in smooth, grey stone, punctuated by a discreet NOR logo in brushed steel. Potted olive trees flank the entrance, adding a touch of organic warmth. The surrounding street is clean and upscale, hinting at the exclusivity of the location. A valet stand is subtly positioned to the side, further emphasizing the premium experience. - Late Afternoon, Autumn

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Wide shot of Ethan at the entrance. Text overlay: 'NOR – Elegance Fitted. Book your fitting. Walk in distinction.' Focus on his confident stance.
Camera Movement: Smooth, cinematic movement supporting the Assured mood
Lighting: Professional lighting matching Stylized Corporate Elegance and Late Afternoon
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Assured emotion
SFX: Music swells and then fades slightly. Subtle sounds of the city outside.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Assured

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        }
      ]
    }
  },
  {
    id: '90082895-cac3-4ad1-b470-b48ba885370e',
    title: 'Laboratory safety -2D',
    description: 'Educate your team with engaging content',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/1765700188301-3fd7c7ff8_17630419451.mp4',
    thumbnail_url: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300&fit=crop',
    prompt_text: null,
    duration: '90s',
    display_order: 2,
    scenes_data: {
      totalScenes: 6,
      scenes: [
        {
          sceneNumber: 1,
          title: 'Introduction to Lab Safety',
          content: `OPENING (0-2 seconds):
Subjects:
- Safety Officer: The Safety Officer is illustrated as a mid-30s Caucasian woman with a smooth, fair complexion and shoulder-length, straight brown hair styled simply without any intricate details. She dons a fitted lab coat that is a soft pastel orange, enhancing her approachable demeanor, over a solid blue smart casual shirt. Her black pants are clean and straight-legged, optimized for a professional look. She stands with a confident, upright posture, exuding an air of authority while maintaining a friendly presence. Her simplified facial features include raised brows and a gentle smile, conveying seriousness balanced with approachability, perfectly aligned with her role in promoting safety. Safety Officer speaks with a professional and measured, balanced, clear articulation pace, reflecting moderate energy.
  Expression: neutral, natural expression
  Pose: Based on action - The Safety Officer straightens her lab coat and looks directly at the camera, ready to speak. She gestures toward the various safety posters in the background.

ENVIRONMENT:
Location: Development Lab
The Development Lab is depicted as a bright, spacious room with clean, flat white walls that create a professional backdrop. The floor is rendered in a soft pastel beige (#F2E6D8), enhancing the neat and organized feel of the environment. Modern lab equipment is represented using simple geometric shapes, featuring stark outlines and bold colors to signify different tools while maintaining a uniform aesthetic.

Workstations are arranged symmetrically across the space, each represented by color block tables with flat surfaces in warm orange (#F8A23E). Above each workstation, strategically placed panels display safety posters using minimalistic graphic icons, offering clear and informative visuals.

Color-coded bins are illustrated as flat rectangular shapes in muted tones, creating a clean and orderly sense of waste management. The overall atmosphere is structured, exuding a feeling of diligence and safety while reinforcing the importance of a meticulous workspace devoid of people, emphasizing the lab's role as a hub of productivity.
Atmosphere: The tone is serious and focused while fostering a sense of safety and diligence. The setting encourages responsible behavior and reinforces the importance of safety in scientific work.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
The Safety Officer straightens her lab coat and looks directly at the camera, ready to speak. She gestures toward the various safety posters in the background.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Serious emotional tone.

CAMERA:
Shot: Medium shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Lighting: Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.
Composition: The camera focuses on the Safety Officer, with soft, even lighting highlighting her face. The background shows safety posters clearly visible.

VISUAL STYLE:
Style: 2D Flat Explainer Style
Mood: Bright

🎨 ANIMATION DIRECTION (High-quality 2D Motion Design):
- Layered parallax depth between foreground, midground, and background
- Natural easing in/out on all object and character motion
- Smart anticipation and follow-through (slight overshoot on movement)
- Consistent motion arcs and squash-and-stretch for expressive energy
- Maintain clean flat colors and bold shapes (no gradients)
- Add dynamic transitions: morphs, wipes, match-cuts, and camera pans
- Emphasize rhythm — sync motion timing to narration beats
- Use limited shadows or depth cues to create dimension without realism

AUDIO:
Dialogue: Safety Officer (professional and measured): 'Welcome to our lab. Today, we're going over essential safety rules for a secure working environment.'
SFX: Background ambient sounds of a quiet lab, slight music building tension.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Serious

AVOID:

- No text overlays or subtitles
- No unrealistic movements or physics violations
- No distorted faces or unnatural proportions
- No inconsistent lighting across shots
- No floating objects or perspective errors
- No exaggerated expressions or uncanny valley effects
- No dramatic lens flares or artificial effects
- No watermarks or branding overlays
- No abrupt cuts or jarring transitions
- No audio sync issues
- No background music (allow ambient environmental sounds only)`
        },
        {
          sceneNumber: 2,
          title: 'Know Your PPE',
          content: `OPENING (0-2 seconds):
Subjects:
- Safety Officer: The Safety Officer is illustrated as a mid-30s Caucasian woman with a smooth, fair complexion and shoulder-length, straight brown hair styled simply without any intricate details. She dons a fitted lab coat that is a soft pastel orange, enhancing her approachable demeanor, over a solid blue smart casual shirt. Her black pants are clean and straight-legged, optimized for a professional look. She stands with a confident, upright posture, exuding an air of authority while maintaining a friendly presence. Her simplified facial features include raised brows and a gentle smile, conveying seriousness balanced with approachability, perfectly aligned with her role in promoting safety. Safety Officer speaks with a professional and measured, balanced, clear articulation pace, reflecting moderate energy.
  Expression: neutral, natural expression
  Pose: Based on action - She picks up a pair of safety goggles and demonstrates how to put them on properly. Then, she shows gloves and a lab coat.

ENVIRONMENT:
Location: Development Lab
The Development Lab is depicted as a bright, spacious room with clean, flat white walls that create a professional backdrop. The floor is rendered in a soft pastel beige (#F2E6D8), enhancing the neat and organized feel of the environment.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
She picks up a pair of safety goggles and demonstrates how to put them on properly. Then, she shows gloves and a lab coat.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Instructive emotional tone.

CAMERA:
Shot: Close-up shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Lighting: Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.
Composition: Close-up on hands holding PPE equipment, then zoom out to show the Safety Officer demonstrating proper use.

VISUAL STYLE:
Style: 2D Flat Explainer Style
Mood: Bright

🎨 ANIMATION DIRECTION (High-quality 2D Motion Design):
- Layered parallax depth between foreground, midground, and background
- Natural easing in/out on all object and character motion
- Smart anticipation and follow-through (slight overshoot on movement)
- Consistent motion arcs and squash-and-stretch for expressive energy

AUDIO:
Dialogue: Safety Officer (professional and measured): 'Personal Protective Equipment, or PPE, is your first line of defense. Always wear goggles to protect your eyes, gloves to shield your hands, and a lab coat.'
SFX: Sounds of equipment being handled, slight rustling of fabric.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Instructive

AVOID:

- No text overlays or subtitles
- No unrealistic movements or physics violations
- No distorted faces or unnatural proportions
- No inconsistent lighting across shots
- No floating objects or perspective errors`
        },
        {
          sceneNumber: 3,
          title: 'Handling Hazardous Materials',
          content: `OPENING (0-2 seconds):
Subjects:
- Safety Officer: The Safety Officer is illustrated as a mid-30s Caucasian woman with a smooth, fair complexion and shoulder-length, straight brown hair styled simply without any intricate details. She dons a fitted lab coat that is a soft pastel orange, enhancing her approachable demeanor, over a solid blue smart casual shirt.
  Expression: neutral, natural expression
  Pose: Based on action - She points to a cabinet with hazard symbols, then demonstrates proper handling technique using tongs and a secure container.

ENVIRONMENT:
Location: Development Lab
The Development Lab is depicted as a bright, spacious room with clean, flat white walls that create a professional backdrop.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
She points to a cabinet with hazard symbols, then demonstrates proper handling technique using tongs and a secure container.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Cautious emotional tone.

CAMERA:
Shot: Medium shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Composition: Shows the cabinet with hazard symbols, then follows the Safety Officer's hands as she demonstrates proper handling.

VISUAL STYLE:
Style: 2D Flat Explainer Style
Mood: Bright

AUDIO:
Dialogue: Safety Officer (professional and measured): 'When handling hazardous materials, always use the proper tools. Never use bare hands, and always store chemicals in designated areas.'
SFX: Sound of cabinet door opening, tongs clicking.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Cautious

AVOID:

- No text overlays or subtitles
- No unrealistic movements or physics violations`
        },
        {
          sceneNumber: 4,
          title: 'Emergency Procedures',
          content: `OPENING (0-2 seconds):
Subjects:
- Safety Officer: The Safety Officer is illustrated as a mid-30s Caucasian woman with a smooth, fair complexion and shoulder-length, straight brown hair styled simply without any intricate details.
  Expression: neutral, natural expression
  Pose: Based on action - She walks over to the emergency shower and eyewash station, pointing out their locations. She then gestures toward the fire extinguisher.

ENVIRONMENT:
Location: Development Lab
The Development Lab is depicted as a bright, spacious room with emergency equipment clearly visible.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
She walks over to the emergency shower and eyewash station, pointing out their locations. She then gestures toward the fire extinguisher.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Alert emotional tone.

CAMERA:
Shot: Wide shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Composition: Wide shot showing emergency equipment locations, with the Safety Officer moving between them.

VISUAL STYLE:
Style: 2D Flat Explainer Style
Mood: Bright

AUDIO:
Dialogue: Safety Officer (professional and measured): 'Know where emergency equipment is located. The eyewash station, emergency shower, and fire extinguisher could save a life—including yours.'
SFX: Slight echo of the lab environment, footsteps.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Alert

AVOID:

- No text overlays or subtitles
- No unrealistic movements or physics violations`
        },
        {
          sceneNumber: 5,
          title: 'Empowerment Through Compliance',
          content: `OPENING (0-2 seconds):
Subjects:
- Safety Officer: The Safety Officer is illustrated as a mid-30s Caucasian woman with a smooth, fair complexion and shoulder-length, straight brown hair styled simply without any intricate details.
  Expression: neutral, natural expression
  Pose: Based on action - She stands confidently, arms slightly open in a welcoming gesture. She nods reassuringly.

ENVIRONMENT:
Location: Development Lab
The Development Lab is depicted as a bright, spacious room with clean, flat white walls.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
She stands confidently, arms slightly open in a welcoming gesture. She nods reassuringly.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Reassuring emotional tone.

CAMERA:
Shot: Medium shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Composition: The camera focuses on the Safety Officer, capturing her reassuring presence.

VISUAL STYLE:
Style: 2D Flat Explainer Style
Mood: Bright

AUDIO:
Dialogue: Safety Officer (professional and measured): 'Following these guidelines isn't just about rules—it's about protecting yourself and your colleagues. Safety empowers us all.'
SFX: Calming ambient sounds, subtle positive music cue.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Reassuring

AVOID:

- No text overlays or subtitles
- No unrealistic movements or physics violations`
        },
        {
          sceneNumber: 6,
          title: 'Our Collective Commitment',
          content: `OPENING (0-2 seconds):
Subjects:
- Safety Officer: The Safety Officer is illustrated as a mid-30s Caucasian woman with a smooth, fair complexion and shoulder-length, straight brown hair styled simply without any intricate details.
  Expression: neutral, natural expression
  Pose: Based on action - She smiles warmly, inviting everyone to commit to safety together, encouraging a unified approach.

ENVIRONMENT:
Location: Development Lab
The Development Lab is depicted as a bright, spacious room with clean, flat white walls.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
She smiles warmly, inviting everyone to commit to safety together, encouraging a unified approach.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Secure emotional tone.

CAMERA:
Shot: Medium shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Composition: The camera slowly zooms out to encompass the entire lab environment, emphasizing unity and collective responsibility.

VISUAL STYLE:
Style: 2D Flat Explainer Style
Mood: Bright

AUDIO:
Dialogue: Safety Officer (professional and measured): 'Let's commit ourselves to safety—together, we can create a secure environment for scientific progress.'
SFX: A triumphant and hopeful musical score fades in, giving a sense of closure and motivation.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Secure

AVOID:

- No text overlays or subtitles
- No unrealistic movements or physics violations`
        }
      ]
    }
  },
  {
    id: '62b7b586-beab-4876-b235-bbfb17c5004e',
    title: 'Unboxing perfume',
    description: 'Tell your brand narrative compellingly',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/1765700480106-3b6184952_Perfumeadvertisementforsocialmedia.mp4',
    thumbnail_url: null,
    prompt_text: null,
    duration: '120s',
    display_order: 3,
    scenes_data: {
      totalScenes: 6,
      scenes: [
        {
          sceneNumber: 1,
          title: 'The Personal Whisper',
          content: `Subject:
Elara: Fair skin with a luminous, healthy complexion. Her hair is long, wavy, and a natural sun-kissed blonde, styled effortlessly to frame her face. She wears a tailored, blush-pink silk blouse paired with high-waisted cream trousers, exuding understated luxury. Her posture is graceful and confident, with a subtle curve to her spine. Her facial features are soft yet defined, with expressive eyes and a gentle, knowing smile. Her hands are delicate, with perfectly manicured, polished nude-pink nails. Her overall look is sophisticated and modern, reflecting a woman who is comfortable in her own skin, adapted for a photorealistic, live-action aesthetic.
ThinkPink Perfume: [AI Suggested] ThinkPink Perfume at index 0: sleek, modern design with premium materials and intuitive interface.

Action: Elara sits gracefully at her vanity, picking up the blush-pink ThinkPink Perfume bottle. She holds it with a gentle, knowing smile.

Dialogue:
Elara: "This isn't just a scent. It's a whisper of something deeply personal, a secret shared."

Context: Elara introduces the ThinkPink Perfume, holding it delicately.

Location: A pristine white vanity table with a smooth, polished surface, reflecting soft light. The background is a subtly blurred, chic bedroom interior featuring muted tones of cream and soft grey, perhaps a glimpse of a plush headboard or a minimalist art piece. The overall aesthetic is clean, luxurious, and uncluttered, emphasizing a sense of calm and personal space. The textures are refined, from the smooth wood of the table to the soft fabrics in the background, all rendered photorealistically.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Close-up on Elara's hands as she picks up the bottle. Soft, warm lighting. Focus on the perfume's elegant design.
Camera Movement: Smooth, cinematic movement supporting the Introspection mood
Lighting: Professional lighting matching Emotive Product Drama
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Elara: "This isn't just a scent. It's a whisper of something deeply personal, a secret shared."
Music: Subtle background music enhancing Introspection emotion
SFX: Soft, ethereal music begins, subtle and inviting.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Introspection

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 2,
          title: 'Elegance in Hand',
          content: `Subject:
Elara: Fair skin with a luminous, healthy complexion. Her hair is long, wavy, and a natural sun-kissed blonde, styled effortlessly to frame her face.
ThinkPink Perfume: sleek, modern design with premium materials and intuitive interface.

Action: Elara sits gracefully at her vanity, picking up the blush-pink ThinkPink Perfume bottle. She holds it with a gentle, knowing smile.

Dialogue:
Elara: "This isn't just a scent. It's a whisper of something deeply personal, a secret shared."

Context: Elara introduces the ThinkPink Perfume, holding it delicately.

Location: A pristine white vanity table with a smooth, polished surface, reflecting soft light.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Close-up on Elara's hands as she picks up the bottle. Soft, warm lighting. Focus on the perfume's elegant design.
Camera Movement: Smooth, cinematic movement supporting the Introspection mood
Lighting: Professional lighting matching Emotive Product Drama
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Elara: "This isn't just a scent. It's a whisper of something deeply personal, a secret shared."
Music: Subtle background music enhancing Introspection emotion
SFX: Soft, ethereal music begins, subtle and inviting.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Introspection

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 3,
          title: 'The Sensory Ritual',
          content: `Subject:
Elara: Fair skin with a luminous, healthy complexion. Her hair is long, wavy, and a natural sun-kissed blonde, styled effortlessly to frame her face.
ThinkPink Perfume: sleek, modern design with premium materials and intuitive interface.

Action: Elara delicately sprays the perfume onto her neck and wrist, her movements fluid and elegant.

Dialogue:
Elara: "Classic yet bold notes. It empowers me, truly, to be my most authentic self."

Context: Elara applies the ThinkPink Perfume, articulating its empowering notes.

Location: A pristine white vanity table with a smooth, polished surface, reflecting soft light.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Close-up on the perfume mist, then on Elara's graceful application to her pulse points.
Camera Movement: Smooth, cinematic movement supporting the Sensory pleasure mood
Lighting: Professional lighting matching Emotive Product Drama
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Elara: "Classic yet bold notes. It empowers me, truly, to be my most authentic self."
Music: Subtle background music enhancing Sensory pleasure emotion
SFX: A delicate spritz sound effect. Music becomes more vibrant, yet still elegant.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Sensory pleasure

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 4,
          title: 'The Inner Glow',
          content: `Subject:
Elara: Fair skin with a luminous, healthy complexion. Her hair is long, wavy, and a natural sun-kissed blonde, styled effortlessly to frame her face.
ThinkPink Perfume: sleek, modern design with premium materials and intuitive interface.

Action: Elara gazes into a mirror, a confident and radiant smile spreading across her face, feeling the transformation.

Dialogue:
Elara: "This scent isn't just worn; it's embodied. It's my confidence, my true essence."

Context: Elara looks in the mirror, transformed and confident by the scent.

Location: A pristine white vanity table with a smooth, polished surface, reflecting soft light.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Over-the-shoulder shot of Elara looking into the mirror, reflecting her confident expression.
Camera Movement: Smooth, cinematic movement supporting the Confidence mood
Lighting: Professional lighting matching Emotive Product Drama
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Elara: "This scent isn't just worn; it's embodied. It's my confidence, my true essence."
Music: Subtle background music enhancing Confidence emotion
SFX: Music becomes more uplifting and empowering.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Confidence

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 5,
          title: 'Radiant Reflection',
          content: `Subject:
Elara: Fair skin with a luminous, healthy complexion. Her hair is long, wavy, and a natural sun-kissed blonde, styled effortlessly to frame her face.
ThinkPink Perfume: sleek, modern design with premium materials and intuitive interface.

Action: Elara gazes into a mirror, a confident and radiant smile spreading across her face, feeling the transformation.

Dialogue:
Elara: "This scent isn't just worn; it's embodied. It's my confidence, my true essence."

Context: Elara looks in the mirror, transformed and confident by the scent.

Location: A pristine white vanity table with a smooth, polished surface, reflecting soft light.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Over-the-shoulder shot of Elara looking into the mirror, reflecting her confident expression.
Camera Movement: Smooth, cinematic movement supporting the Confidence mood
Lighting: Professional lighting matching Emotive Product Drama
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Elara: "This scent isn't just worn; it's embodied. It's my confidence, my true essence."
Music: Subtle background music enhancing Confidence emotion
SFX: Music becomes more uplifting and empowering.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Confidence

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 6,
          title: 'The Final Note',
          content: `Subject:
Elara: Fair skin with a luminous, healthy complexion. Her hair is long, wavy, and a natural sun-kissed blonde, styled effortlessly to frame her face.
ThinkPink Perfume: sleek, modern design with premium materials and intuitive interface.

Action: Final shot of the ThinkPink Perfume bottle with elegant lighting and call to action.

Dialogue:
Elara: "ThinkPink. Embrace your true self."

Context: Final branding shot with product focus.

Location: A pristine white vanity table with a smooth, polished surface, reflecting soft light.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Elegant product shot with soft lighting and bokeh background.
Camera Movement: Slow push toward the bottle
Lighting: Professional lighting matching Emotive Product Drama
Lens: 85mm equivalent, shallow depth of field

Audio:
Dialogue:
Elara: "ThinkPink. Embrace your true self."
Music: Music swells to conclusion
SFX: Soft chime as logo appears

Technical Specs:
Duration: 5
Aspect Ratio: 16:9
Emotional State: Aspirational

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        }
      ]
    }
  },
  {
    id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    title: 'Communication in the workplace',
    description: 'Learn effective communication strategies',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/videos/1768815465852-ypyw8.mp4',
    thumbnail_url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop',
    prompt_text: null,
    duration: '105s',
    display_order: 4,
    scenes_data: {
      totalScenes: 7,
      scenes: [
        {
          sceneNumber: 1,
          title: 'Opening - The Communication Challenge',
          content: `Subject:
Sarah: A professional woman in her mid-30s, wearing smart business casual attire. She has warm brown eyes and an approachable demeanor.

Action: Sarah addresses the camera directly, introducing the topic of workplace communication.

Dialogue:
Sarah: "Effective communication is the foundation of every successful team."

Context: Introduction to workplace communication training.

Location: Modern open-plan office with collaborative spaces visible in the background.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Medium shot with shallow depth of field
Camera Movement: Slow push-in
Lighting: Soft, professional lighting
Lens: 35mm equivalent

Audio:
Dialogue: Sarah introduces the topic
Music: Upbeat corporate background
SFX: Subtle office ambiance

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Engaging`
        },
        {
          sceneNumber: 2,
          title: 'Active Listening',
          content: `Subject:
Sarah: A professional woman in her mid-30s, wearing smart business casual attire.
Team members in background

Action: Sarah demonstrates active listening techniques with a colleague.

Dialogue:
Sarah: "Active listening means giving your full attention and showing you understand."

Context: Demonstrating active listening skills.

Location: Meeting room with glass walls.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Two-shot showing interaction
Camera Movement: Static with slight pan
Lighting: Natural office lighting
Lens: 50mm equivalent

Audio:
Dialogue: Sarah explains active listening
Music: Continuing background track
SFX: Meeting room ambiance

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Attentive`
        },
        {
          sceneNumber: 3,
          title: 'Clear and Concise',
          content: `Subject:
Sarah demonstrating clear communication

Action: Sarah shows how to structure messages clearly.

Dialogue:
Sarah: "Be clear, be concise, and always confirm understanding."

Context: Teaching clear communication principles.

Location: Modern office workspace.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Medium shot
Camera Movement: Subtle movement
Lighting: Professional
Lens: 35mm

Audio:
Dialogue: Sarah's instruction
Music: Background continues
SFX: Office sounds

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Instructive`
        },
        {
          sceneNumber: 4,
          title: 'Non-Verbal Communication',
          content: `Subject:
Sarah demonstrating body language

Action: Sarah explains the importance of non-verbal cues.

Dialogue:
Sarah: "Your body language speaks as loudly as your words."

Context: Understanding non-verbal communication.

Location: Open office space.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Full body shot
Camera Movement: Following movement
Lighting: Even lighting
Lens: 35mm

Audio:
Dialogue: Sarah's explanation
Music: Subtle background
SFX: Ambient office

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Aware`
        },
        {
          sceneNumber: 5,
          title: 'Handling Difficult Conversations',
          content: `Subject:
Sarah in a meeting setting

Action: Sarah provides tips for difficult conversations.

Dialogue:
Sarah: "Approach difficult conversations with empathy and openness."

Context: Navigating challenging discussions.

Location: Private meeting room.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Close-up for intimacy
Camera Movement: Static
Lighting: Soft, warm
Lens: 50mm

Audio:
Dialogue: Sarah's advice
Music: Reflective tone
SFX: Quiet room ambiance

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Empathetic`
        },
        {
          sceneNumber: 6,
          title: 'Digital Communication',
          content: `Subject:
Sarah at a computer

Action: Sarah discusses effective digital communication.

Dialogue:
Sarah: "In digital communication, tone is everything. Choose your words carefully."

Context: Best practices for email and messaging.

Location: Personal workspace with computer.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Over-the-shoulder with screen visible
Camera Movement: Slow orbit
Lighting: Screen glow mixed with office light
Lens: 35mm

Audio:
Dialogue: Sarah's guidance
Music: Modern, digital feel
SFX: Keyboard typing, notification sounds

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Thoughtful`
        },
        {
          sceneNumber: 7,
          title: 'Closing - Building Connections',
          content: `Subject:
Sarah with team in background

Action: Sarah concludes with the importance of communication for team success.

Dialogue:
Sarah: "Great communication builds trust, drives collaboration, and creates success."

Context: Inspiring conclusion.

Location: Open office with team visible.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Wide shot showing team dynamic
Camera Movement: Slow pull-back
Lighting: Warm, inviting
Lens: 24mm

Audio:
Dialogue: Sarah's closing statement
Music: Uplifting conclusion
SFX: Team activity sounds

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Inspired`
        }
      ]
    }
  },
  {
    id: 'b2c3d4e5-f6a7-8901-bcde-f23456789012',
    title: 'Laboratory safety',
    description: 'Comprehensive lab safety training',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/1765700429690-be591e7e1_17630299152.mp4',
    thumbnail_url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop',
    prompt_text: null,
    duration: '90s',
    display_order: 5,
    scenes_data: {
      totalScenes: 6,
      scenes: [
        {
          sceneNumber: 1,
          title: 'Welcome and Introduction',
          content: `Subject:
Dr. Emily Carter: 40-year-old female, Caucasian, Dr. Emily Carter stands at approximately 5'7" with an athletic yet approachable build. Her face is framed by soft, shoulder-length auburn hair, often styled in loose waves that convey both professionalism and warmth. Her eyes are a clear, inviting hazel, accentuated by subtle, natural-looking makeup. She has a warm, genuine smile that crinkles the corners of her eyes, suggesting a kind and empathetic nature.

Action: Dr. Carter greets the viewer from a modern kitchen, holding a medication box.

Dialogue:
Dr. Emily Carter: "Hi, I'm Dr. Emily Carter. Today, I'll guide you through proper medication usage."

Context: Introduction to medication guidance video.

Location: A bright, modern kitchen with clean countertops and minimalist decor.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Medium shot with professional framing
Camera Movement: Slow push-in
Lighting: Natural morning light
Lens: 35mm equivalent

Audio:
Dialogue: Dr. Carter's welcoming introduction
Music: Calm, professional background
SFX: Kitchen ambient sounds

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Welcoming`
        },
        {
          sceneNumber: 2,
          title: 'Understanding Your Medication',
          content: `Subject:
Dr. Emily Carter explaining medication information
MediX 250mg Box displayed

Action: Dr. Carter explains how to read medication labels.

Dialogue:
Dr. Emily Carter: "Always read the label carefully. Note the dosage, warnings, and expiration date."

Context: Teaching label reading skills.

Location: Modern kitchen with good lighting.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Close-up on medication box, then to Dr. Carter
Camera Movement: Smooth transition
Lighting: Professional lighting
Lens: 50mm for detail shots

Audio:
Dialogue: Dr. Carter's explanation
Music: Continuing background
SFX: Package handling sounds

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Informative`
        },
        {
          sceneNumber: 3,
          title: 'Proper Dosage Guidance',
          content: `Subject:
Dr. Emily Carter demonstrating dosage

Action: Dr. Carter demonstrates proper dosage measurement.

Dialogue:
Dr. Emily Carter: "Take one tablet with a full glass of water, as directed. For example, take it every morning before breakfast."

Context: Demonstrating correct dosage.

Location: Kitchen with water glass and medication.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Action-focused shots
Camera Movement: Following hands
Lighting: Clear, bright
Lens: 35mm

Audio:
Dialogue: Dr. Carter's instruction
Music: Subtle background
SFX: Water pouring, pill placement

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Instructive`
        },
        {
          sceneNumber: 4,
          title: 'Safety First – Precautions',
          content: `Subject:
Dr. Emily Carter in her office

Action: Dr. Carter addresses common concerns and emphasizes consistency.

Dialogue:
Dr. Emily Carter: "Starting a new medication can be overwhelming, but consistency is key. Set reminders to help you remember your doses."

Context: Addressing common concerns.

Location: Doctor's office with medical charts visible.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Soft, flattering lighting with blurred background
Camera Movement: Smooth push-in
Lighting: Professional office lighting
Lens: 35mm

Audio:
Dialogue: Dr. Carter's reassurance
Music: Calming background
SFX: Office ambiance

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Reassuring`
        },
        {
          sceneNumber: 5,
          title: 'Daily Integration & Routine',
          content: `Subject:
Dr. Emily Carter consulting

Action: Dr. Carter emphasizes professional medical advice.

Dialogue:
Dr. Emily Carter: "If you experience any unexpected side effects or have concerns, please contact your doctor or pharmacist immediately."

Context: Recommending professional consultation.

Location: Doctor's office.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Close-up for sincerity
Camera Movement: Static
Lighting: Professional
Lens: 50mm

Audio:
Dialogue: Dr. Carter's advice
Music: Slight increase in intensity
SFX: Minimal

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Empathetic`
        },
        {
          sceneNumber: 6,
          title: 'Partnering for Your Health',
          content: `Subject:
Dr. Emily Carter with MediX box

Action: Final summary and call to action.

Dialogue:
Dr. Emily Carter: "Follow the instructions, consult the leaflet, or visit our website for more information."

Context: Summary and closing.

Location: Kitchen setting again.

Visual Style: Stylized Corporate Elegance

Cinematography:
Composition: Warm lighting, similar to opening
Camera Movement: Smooth
Lighting: Natural morning light
Lens: 35mm

Audio:
Dialogue: Dr. Carter's closing
Music: Uplifting finale
SFX: Gentle whooshing effect

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Confident`
        }
      ]
    }
  },
  {
    id: '16d46ec3-865a-474b-8ca9-691b73e3d63f',
    title: 'Cybersecurity -3D',
    description: 'Cloud security education in 3D style',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/1765700631658-295e08301_17635500561.mp4',
    thumbnail_url: null,
    prompt_text: null,
    duration: '75s',
    display_order: 7,
    scenes_data: {
      totalScenes: 6,
      scenes: [
        {
          sceneNumber: 1,
          title: 'Welcome to the Digital Cloud',
          content: `OPENING (0-2 seconds):
Subjects:
- Ben: Ben is depicted as an adorable, plump cloud character, enveloped in a soft pastel pink gradient that subtly transitions through lighter and darker shades to create a fluffy, inviting texture. He radiates a warm, gentle glow, enhancing his friendly demeanor. His eyes are oversized and sparkling cerulean, exuding a sense of wonder and care, while his small mouth is always curled into a welcoming smile that adds to his charm.
  Expression: neutral, natural expression
  Pose: Based on action - Ben floats into the center of the scene, gently drifting through fluffy pastel clouds while waving at the audience.

ENVIRONMENT:
Location: Digital Cloud Environment
The Digital Cloud Environment envelops the viewer in a whimsical 3D realm composed of billowy soft clouds rendered in vibrant shades of pastel blue and delicate pink. A cinematic three-point lighting setup bathes the scene, with warm amber key lights casting gentle shadows that enhance the floating cloud layers.

Lighting:
Even, vibrant daylight with balanced contrast, full of energy, cheerfulness, and clarity.

MAIN ACTION (2-5 seconds):
Ben floats into the center of the scene, gently drifting through fluffy pastel clouds while waving at the audience. He looks cheerful and ready to engage.

RESOLUTION (5-8 seconds):
Scene concludes with natural, appropriate emotional resolution. Scene concludes with Friendly emotional tone.

CAMERA:
Shot: Medium shot
Lens: 35mm equivalent
Depth of Field: Medium
Movement: Smooth push-in
Composition: Ben is well-lit by warm amber key lights, giving him an inviting glow.

VISUAL STYLE:
Style: 3D Soft Gradient Style
Mood: Bright

🎬 ANIMATION DIRECTION (Pixar-style 3D):
- Cinematic lighting with warm bounce and volumetric fog
- Soft global illumination and subsurface scattering for materials
- Expressive character animation: micro-expressions, body language, eyes alive
- Story-driven camera motion (slow dolly, tilt, or pan to emotion)

AUDIO:
Dialogue: Ben (playful and educational): 'Hi there! I'm Ben, and I'm here to help you keep your cloud data safe!'
SFX: Soft, whimsical background music plays, accompanied by gentle wind sounds.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Frame Rate: 24fps
Emotion: Friendly`
        },
        {
          sceneNumber: 2,
          title: 'Password Protection',
          content: `OPENING (0-2 seconds):
Subjects:
- Ben: Adorable cloud character with pastel pink gradient.
  Expression: neutral, natural expression
  Pose: Based on action - Ben hovers slightly while gesturing emphatically with his hands as he explains the significance of strong passwords.

ENVIRONMENT:
Location: Digital Cloud Environment
Whimsical 3D realm with floating clouds and security icons.

MAIN ACTION (2-5 seconds):
Ben hovers slightly while gesturing emphatically with his hands as he explains the significance of strong passwords.

CAMERA:
Shot: Wide shot
Lens: 35mm equivalent
Composition: The camera zooms in on Ben's expressive face.

VISUAL STYLE:
Style: 3D Soft Gradient Style
Mood: Bright

AUDIO:
Dialogue: Ben (playful and educational): 'The first step in securing your cloud is choosing a strong password! It's like the key to your data vault.'
SFX: Background music takes on a slightly more serious tone.

TECHNICAL:
Duration: 8 seconds
Aspect Ratio: 16:9
Emotion: Informative`
        },
        {
          sceneNumber: 3,
          title: 'Two-Factor Authentication',
          content: `Subject:
Ben explaining 2FA concepts

Action: Ben demonstrates the concept of two-factor authentication.

Dialogue:
Ben: "Two-factor authentication adds an extra layer of security. It's like having two locks on your door!"

Context: Teaching 2FA importance.

Location: Digital Cloud Environment with security shield icons.

Visual Style: 3D Soft Gradient Style

Cinematography:
Composition: Dynamic shots showing security concepts
Camera Movement: Smooth orbiting
Lighting: Bright, friendly

Audio:
Dialogue: Ben's explanation
Music: Engaging background
SFX: Lock sounds, digital effects

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Educational`
        },
        {
          sceneNumber: 4,
          title: 'Recognizing Threats',
          content: `Subject:
Ben with warning icons

Action: Ben explains how to recognize potential security threats.

Dialogue:
Ben: "Watch out for suspicious emails and links. When in doubt, don't click!"

Context: Threat awareness training.

Location: Digital Cloud Environment with caution elements.

Visual Style: 3D Soft Gradient Style

Cinematography:
Composition: Focused shots with warning indicators
Camera Movement: Alert, dynamic movement
Lighting: Slightly more dramatic

Audio:
Dialogue: Ben's warning
Music: More urgent tone
SFX: Alert sounds

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Cautious`
        },
        {
          sceneNumber: 5,
          title: 'Safe Practices',
          content: `Subject:
Ben demonstrating safe practices

Action: Ben summarizes best practices for cloud security.

Dialogue:
Ben: "Regular updates, strong passwords, and awareness – these are your best friends in cloud security!"

Context: Best practices summary.

Location: Digital Cloud Environment with checklist visuals.

Visual Style: 3D Soft Gradient Style

Cinematography:
Composition: Organized, clear shots
Camera Movement: Steady
Lighting: Bright and positive

Audio:
Dialogue: Ben's summary
Music: Uplifting
SFX: Checkmark sounds

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Confident`
        },
        {
          sceneNumber: 6,
          title: 'Staying Safe Together',
          content: `Subject:
Ben waving goodbye

Action: Ben concludes with an encouraging message.

Dialogue:
Ben: "Remember, staying safe online is a team effort. Together, we can protect our digital world!"

Context: Closing and encouragement.

Location: Digital Cloud Environment with community visuals.

Visual Style: 3D Soft Gradient Style

Cinematography:
Composition: Wide shot with Ben centered
Camera Movement: Slow pull-back
Lighting: Warm, friendly

Audio:
Dialogue: Ben's farewell
Music: Triumphant conclusion
SFX: Magical chimes

Technical Specs:
Duration: 8
Aspect Ratio: 16:9
Emotional State: Hopeful`
        }
      ]
    }
  },
  {
    id: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    title: 'Advertisement for VIVARA shoes',
    description: 'Empowering women through movement',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/1765700385159-2a9cb0a20_AdvertisementForRunningShoes.mp4',
    thumbnail_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop',
    prompt_text: null,
    duration: '60s',
    display_order: 8,
    scenes_data: {
      totalScenes: 6,
      scenes: [
        {
          sceneNumber: 1,
          title: 'Dawn on the Trail',
          content: `Subject:
Anya Petrova: 32-year-old female, Mixed European, Anya stands at 5'8" with a lean, athletic build honed from years of running and outdoor activity. Her skin is a warm tan, kissed by the sun and freckled across her nose and cheekbones. Her eyes are a striking emerald green, framed by thick, dark lashes that accentuate their intensity. Anya's hair is a cascade of dark brown curls, often pulled back into a practical ponytail or braid during runs.

Action: Anya powers up a rocky mountain trail at dawn, red VIVARA Blaze shoes in focus.

Dialogue:


Context: Opening establishing shot with product focus.

Location: A steep, rocky mountain trail winds its way through a lush forest. The rising sun casts a golden glow over the landscape.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Golden light, camera follows Anya's stride, focus on red VIVARA Blaze shoes.
Camera Movement: Smooth, cinematic movement supporting the Determination mood
Lighting: Professional lighting matching Emotive Product Drama and Dawn
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Determination emotion
SFX: Sound of rhythmic breathing, birds chirping, wind rustling through trees, inspiring music begins softly.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Determination

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 2,
          title: 'The Rhythm of Resilience',
          content: `Subject:
Anya Petrova: Athletic runner with emerald green eyes and dark brown curls.

Action: Anya navigates a busy cityscape with agility and poise.

Dialogue:


Context: Anya moves confidently through the urban environment.

Location: A vibrant cityscape filled with towering skyscrapers, busy streets, and bustling crowds.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Fast-paced shots, Anya weaving through crowds, close-up on her shoes, vibrant city colors.
Camera Movement: Smooth, cinematic movement supporting the Focus mood
Lighting: Professional lighting matching Emotive Product Drama and Midday
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Focus emotion
SFX: City ambient sounds: car horns, pedestrian chatter, construction noises, music tempo increases slightly.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Focus

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 3,
          title: 'Nature\'s Challenge',
          content: `Subject:
Anya Petrova: Athletic runner reflecting in a park.

Action: Anya pauses in a park, reflecting on her journey.

Dialogue:
Anya Petrova: "Every run has been a journey, pushing me to discover strength I never knew I had."

Context: Anya finds solace and reflects on her personal transformation.

Location: A serene park oasis nestled in the heart of the city.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Close-up on Anya's face, soft lighting, focus on her VIVARA Blaze shoes, serene park backdrop.
Camera Movement: Smooth, cinematic movement supporting the Introspection mood
Lighting: Professional lighting matching Emotive Product Drama and Late Afternoon
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Anya Petrova: "Every run has been a journey, pushing me to discover strength I never knew I had."
Music: Subtle background music enhancing Introspection emotion
SFX: Sound of fountain, gentle breeze, soft music, calming atmosphere.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Introspection

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 4,
          title: 'Connected to the Goal',
          content: `Subject:
Anya Petrova: Athletic runner inspiring others.

Action: Anya addresses the audience directly, speaking about empowerment.

Dialogue:
Anya Petrova: "Embrace movement, break those boundaries, and feel bold in your own stride. Find your power."

Context: Anya inspires women to embrace movement and break boundaries.

Location: A serene park oasis in the city.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Direct eye contact with the camera, warm and inviting expression, natural lighting.
Camera Movement: Smooth, cinematic movement supporting the Empowerment mood
Lighting: Professional lighting matching Emotive Product Drama and Late Afternoon
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:
Anya Petrova: "Embrace movement, break those boundaries, and feel bold in your own stride. Find your power."
Music: Subtle background music enhancing Empowerment emotion
SFX: Uplifting music, clear and resonant voice-over.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Empowerment

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 5,
          title: 'The Summit View',
          content: `Subject:
VIVARA Blaze: The VIVARA Blaze is a sleek and stylish athletic shoe featuring a vibrant red colorway.

Action: Montage of diverse women moving confidently in various settings.

Dialogue:


Context: A montage showcases women embracing movement worldwide.

Location: Mountain trail and various environments.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Quick cuts, diverse women in motion, showcasing VIVARA shoes in different environments, vibrant colors.
Camera Movement: Smooth, cinematic movement supporting the Inspiration mood
Lighting: Professional lighting matching Emotive Product Drama and Dawn
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Inspiration emotion
SFX: Upbeat and empowering music, sound effects of various activities (running, dancing, hiking).

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Inspiration

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        },
        {
          sceneNumber: 6,
          title: 'Unstoppable Spirit',
          content: `Subject:
VIVARA Blaze: Sleek athletic shoe with vibrant red colorway.

Action: Anya's outstretched hand with a call to action on-screen.

Dialogue:


Context: Final shot with a clear call to action.

Location: Mountain trail at dawn.

Visual Style: Emotive Product Drama

Cinematography:
Composition: Close-up on Anya's hand, VIVARA Blaze shoes visible, text overlay: 'Discover the shoe that moves with you—shop now.'
Camera Movement: Smooth, cinematic movement supporting the Excitement mood
Lighting: Professional lighting matching Emotive Product Drama and Dawn
Lens: 35mm equivalent, appropriate depth of field

Audio:
Dialogue:

Music: Subtle background music enhancing Excitement emotion
SFX: Music swells to a crescendo, a final sound effect of a confident step.

Technical Specs:
Duration: 7
Aspect Ratio: 16:9
Emotional State: Excitement

Negative: no subtitles or on-screen text, no jump cuts, no dramatic lens flares, no unrealistic movements, no motion blur`
        }
      ]
    }
  },
  {
    id: '286f748c-178d-46ac-8d9d-5bcf551c9789',
    title: 'Safety video',
    description: 'Construction site safety training',
    video_url: 'https://storage.googleapis.com/prompt-director-base44/video-examples/videos/1769069647433-1768474736 (1) (1).mp4',
    thumbnail_url: null,
    prompt_text: null,
    duration: null,
    display_order: 9,
    scenes_data: {
      totalScenes: 8,
      scenes: [
        {
          sceneNumber: 1,
          title: 'The Site Leader',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Slow Dolly-In.
Subject: SUBJECT_A walks forward slowly, adjusting his gloves with a steady gaze.
Action: turns torso slightly right, scanning the construction progress.
Atmosphere: clouds drift across dawn sky, light dust swirls near boots.
Lighting: golden light glints off vest, casting long shadows behind him, bathing in warm tones SUBJECT_A's features. Then slow to a natural pause.

Dialogue: SUBJECT_A says: On this site, your gear is your reputation.
Ambient: Distant metallic clang echoes, giving a sense of open industrial space
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Fluid full-body motion. Consistent physics. Smooth tracking. High temporal consistency. Smooth continuous motion. Solid geometry. Non-morphing textures. Coherent lighting flow. Natural motion dynamics.`
        },
        {
          sceneNumber: 2,
          title: 'Gear as Reputation',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Handheld.
Subject: SUBJECT_A slowly blinks, jaw tightens slightly with a weary expression.
Action: takes a deep breath, chest rising as he adjusts posture.
Atmosphere: fine dust particles drift through the background scaffolding light.
Lighting: overhead light shifts, casting moving shadows across his weathered face, interacting with SUBJECT_A's silhouette. Then slow to a natural pause.

Dialogue: SUBJECT_A says: We don't follow rules just to tick boxes. We do it for them.
Ambient: Wind whistles softly through scaffolding, close and personal
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Gentle, controlled movement. Smooth transitions. Stable composition. High temporal consistency. Smooth continuous motion. Solid geometry. Non-morphing textures. Coherent lighting flow. Natural motion dynamics.`
        },
        {
          sceneNumber: 3,
          title: 'Surveying the Progress',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Handheld.
Subject: SUBJECT_A slowly blinks, jaw tightens slightly with a weary expression.
Action: takes a deep breath, chest rising as he adjusts posture.
Atmosphere: fine dust particles drift through the background scaffolding light.
Lighting: overhead light shifts, casting moving shadows across his weathered face, interacting with SUBJECT_A's silhouette. Then slow to a natural pause.

Dialogue: SUBJECT_A says: We don't follow rules just to tick boxes. We do it for them.
Ambient: Wind whistles softly through scaffolding, close and personal
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Gentle, controlled movement. Smooth transitions. Stable composition. High temporal consistency.`
        },
        {
          sceneNumber: 4,
          title: 'Precision in Every Detail',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Handheld.
Subject: SUBJECT_A tightens grip on rebar, chest rises with steady breath.
Action: turns torso slightly right, scanning the rebar layout.
Atmosphere: fine dust motes drift, distant tree leaves sway gently.
Lighting: soft daylight shifts, casting moving shadows across his weathered face, interacting with SUBJECT_A's silhouette. Then slow to a natural pause.

Dialogue: SUBJECT_A says: GripMaster Gloves give you the control needed for the toughest tasks.
Ambient: Natural ambient room tone with subtle presence
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Gentle, controlled movement. Smooth transitions. Stable composition. High temporal consistency.`
        },
        {
          sceneNumber: 5,
          title: 'Safety and Teamwork',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Slow Dolly-In.
Subject: SUBJECT_A eyes glisten and blink slowly, jaw tightens with emotion.
Action: takes a deep breath, chest rises and falls subtly.
Atmosphere: dust motes drift through the warm background bokeh.
Lighting: golden light flickers, casting shifting highlights across his weathered skin, bathing in warm tones SUBJECT_A's features. Then slow to a natural pause.

Dialogue: SUBJECT_A says: Safety isn't a burden. It's the commitment you make to the people waiting at home.
Ambient: Wind whistles softly through scaffolding, close and personal
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Extremely subtle movement. Near-static with micro-shifts only. High temporal lock.`
        },
        {
          sceneNumber: 6,
          title: 'Overcoming the Challenge',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Slow Dolly-In.
Subject: SUBJECT_A steps forward confidently, looking up at the rising structure.
Action: nods affirmatively, eyes tracking the crane's movement overhead.
Atmosphere: clouds drift across sky, crane arm rotates in background.
Lighting: shifting daylight varies reflections on the workers' high-visibility vests, interacting with SUBJECT_A's silhouette. Then slow to a natural pause.

Dialogue: SUBJECT_A says: Professional pride starts with personal protection. It's how we lead by example.
Ambient: Subtle gravel crunch close to the mic as weight shifts
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Fluid full-body motion. Consistent physics. Smooth tracking. High temporal consistency.`
        },
        {
          sceneNumber: 7,
          title: 'The Vision Realized',
          content: `Use the provided image as the first frame.

═══ SUBJECT MAPPING ═══
SUBJECT_A (Elias) = The figure on the left side of the frame, in the foreground wearing navy blue denim shirt [EXISTING - match image exactly]
═══════════════════════

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Slow Dolly-In.
Subject: SUBJECT_A adjusts gloves, nodding slowly with a look of completion.
Action: nods affirmatively, maintaining a proud and steady posture.
Atmosphere: fine dust particles dance in the warm evening air.
Lighting: warm sunset glow deepens, casting longer shadows across his features, interacting with SUBJECT_A's silhouette. Then slow to a natural pause.

Dialogue: SUBJECT_A says: Before you step onto this site, check your gear. Your life depends on it. Stay safe.
Ambient: Distant metallic clang echoes, giving a sense of open industrial space
Voice: Male Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Gentle, controlled movement. Smooth transitions. Stable composition. High temporal consistency.`
        },
        {
          sceneNumber: 8,
          title: 'The Mark of Quality',
          content: `Use the provided image as the first frame.

Keep: SUBJECT_A identity and clothing exactly as in image. Environment unchanged.

Camera: Slow Dolly-In.
Subject: SUBJECT_A subjects nod firmly, maintaining steady eye contact with camera.
Action: left subject steps forward, gesturing toward the viewer authoritatively.
Atmosphere: dust motes drift, background crane rotates slowly through skyline.
Lighting: sunlight intensifies, casting sharp moving shadows across their vests, interacting with SUBJECT_A's silhouette. Then slow to a natural pause.

Dialogue: SUBJECT_A says: Wear your safety gear. Every time.
Ambient: Natural ambient room tone with subtle presence
Voice: Neutral Voice, en-US, Studio Quality, Close-Mic, 48kHz

Negative: identity drift, face morphing, outfit change, temporal flicker, warped hands, text, watermark, split screen.

Quality: Fluid full-body motion. Consistent physics. Smooth tracking. High temporal consistency. Smooth continuous motion. Solid geometry. Non-morphing textures. Coherent lighting flow. Natural motion dynamics.`
        }
      ]
    }
  }
];

// Hero video URLs for fallback
export const HERO_VIDEOS = {
  main: 'https://storage.googleapis.com/prompt-director-base44/video-examples/hero-1765700804984-62b7c5535_AIPromptDirector_CTA1.mp4',
  enterprise: 'https://storage.googleapis.com/prompt-director-base44/video-examples/hero-1769676950052-Scenario-AI_16-9 (1) (2).mp4'
};
